import moment from 'moment';

const SINGLE_BRACE_GROUP_REGEX = /\{([^{}]*)\}/;

// Splits a date token into maximal runs of the same repeated CLDR symbol, e.g. 'yyyyMMdd' -> ['yyyy', 'MM', 'dd']
const SYMBOL_RUN_REGEX = /([yMd])\1*/g;

// Supported CLDR date symbols for the 'Export File Name Template' date token, and their moment.js equivalents.
// Only these exact run lengths are valid per symbol (e.g. 'yyyyy' is not a valid 5-digit year).
const DATE_TOKEN_TO_MOMENT_TOKEN = {
  y: 'YYYY',
  yy: 'YY',
  yyyy: 'YYYY',
  M: 'M',
  MM: 'MM',
  MMM: 'MMM',
  MMMM: 'MMMM',
  d: 'D',
  dd: 'DD',
};

function getSymbolRuns(dateToken) {
  return dateToken.match(SYMBOL_RUN_REGEX) || [];
}

// Returns the contents of the template's single {dateToken} group, or null when the template
// doesn't contain exactly one bracketed group.
export function extractDateToken(template) {
  if (typeof template !== 'string') {
    return null;
  }

  const openBraceCount = (template.match(/{/g) || []).length;
  const closeBraceCount = (template.match(/}/g) || []).length;

  if (openBraceCount !== 1 || closeBraceCount !== 1) {
    return null;
  }

  return template.match(SINGLE_BRACE_GROUP_REGEX)?.[1] ?? null;
}

// A date token is valid only when it's made up entirely of supported CLDR date symbol runs
// (y/yy/yyyy, M/MM/MMM/MMMM, d/dd) with no separators, unsupported letters, or invalid run lengths
// (e.g. 'yyyyy' or 'MMMMM').
export function isValidDateToken(dateToken) {
  if (!dateToken) {
    return false;
  }

  const runs = getSymbolRuns(dateToken);

  return runs.join('') === dateToken && runs.every((run) => run in DATE_TOKEN_TO_MOMENT_TOKEN);
}

export function isValidExportFileNameTemplate(template) {
  if (typeof template !== 'string') {
    return false;
  }

  // No curly braces means no date format validation is required.
  if (!template.includes('{') && !template.includes('}')) {
    return true;
  }

  return isValidDateToken(extractDateToken(template));
}

// Builds a live preview of the exported file name: substitutes the template's date token with
// today's date and appends the given file extension. Returns null when the template is invalid.
export function buildExportFileNamePreview(template, fileExtension) {
  if (!isValidExportFileNameTemplate(template) || !fileExtension) {
    return null;
  }

  const dateToken = extractDateToken(template);

  if (!dateToken) {
    return `${template}.${fileExtension.toLowerCase()}`;
  }

  const momentFormat = getSymbolRuns(dateToken)
    .map((run) => DATE_TOKEN_TO_MOMENT_TOKEN[run])
    .join('');
  const [prefix, suffix] = template.split(`{${dateToken}}`);

  return `${prefix}${moment().format(momentFormat)}${suffix}.${fileExtension.toLowerCase()}`;
}
